import { useEffect, useState } from "react";
import { CAMPO, Recado } from "./ui";
import {
  editarPaginaDoProduto,
  mensagemDoCatalogo,
  type CamposDaPagina,
  type DocumentacaoDoCurso,
} from "../lib/adminCatalogo";

/**
 * Admin › Catálogo › Produtos › o produto › **editar a página** (000306).
 *
 * Desde 25/09 a página do produto é editada AQUI — a decisão 4 de Victor
 * (24/09): "a página do produto deixa de ser espelho do ClickUp". O que veio de
 * lá em 16/09 é o ponto de partida, e a carga não sobrescreve mais.
 *
 * O formulário manda SÓ o que mudou. É o contrato do catálogo — o campo que não
 * vai não muda, e texto vazio limpa —, e é o que faz o razão dizer "editou:
 * valor" em vez de repetir a página inteira a cada salvamento. Quem recusa
 * (resumo vazio, endereço do MEC sem https, número fora da faixa) é o banco,
 * com a frase pronta.
 */

const TEXTOS: { chave: ChaveDeTexto; rotulo: string; linhas?: number; dica?: string }[] = [
  { chave: "resumo", rotulo: "Resumo", linhas: 2, dica: "A linha que a lista de produtos mostra. Obrigatório." },
  { chave: "sobre", rotulo: "Texto principal", linhas: 8, dica: "A apresentação do curso. Obrigatório." },
  { chave: "objetivo", rotulo: "Objetivo geral", linhas: 3 },
  { chave: "publico_alvo", rotulo: "Público-alvo", linhas: 3 },
  { chave: "perfil_egresso", rotulo: "Perfil do egresso", linhas: 3 },
];

const CURTOS: { chave: ChaveDeTexto; rotulo: string; dica?: string }[] = [
  { chave: "titulo_conferido", rotulo: "Título conferido" },
  { chave: "modalidade", rotulo: "Modalidade" },
  { chave: "professor_lider", rotulo: "Professor líder" },
  { chave: "universidade", rotulo: "Universidade" },
  { chave: "valor", rotulo: "Valor", dica: 'Texto, como a página escreve: "12 x R$ 179,90".' },
  { chave: "lancamento", rotulo: "Lançamento" },
  { chave: "mec_url", rotulo: "Credenciamento MEC (endereço)", dica: "Começa com https://." },
];

const NUMEROS: { chave: "carga_horaria_fonte" | "duracao_minima_fonte" | "duracao_maxima_fonte"; rotulo: string }[] = [
  { chave: "carga_horaria_fonte", rotulo: "Carga horária (horas)" },
  { chave: "duracao_minima_fonte", rotulo: "Prazo mínimo (meses)" },
  { chave: "duracao_maxima_fonte", rotulo: "Prazo máximo (meses)" },
];

/** Os campos de TEXTO do rascunho — todos, menos as seções. */
type ChaveDeTexto = Exclude<keyof Rascunho, "secoes">;

type Rascunho = {
  resumo: string;
  sobre: string;
  objetivo: string;
  publico_alvo: string;
  perfil_egresso: string;
  modalidade: string;
  titulo_conferido: string;
  professor_lider: string;
  universidade: string;
  valor: string;
  lancamento: string;
  mec_url: string;
  carga_horaria_fonte: string;
  duracao_minima_fonte: string;
  duracao_maxima_fonte: string;
  secoes: { titulo: string; texto: string }[];
};

function daPagina(doc: DocumentacaoDoCurso | null): Rascunho {
  const t = (v: string | null | undefined) => v ?? "";
  const n = (v: number | null | undefined) => (v == null ? "" : String(v));
  return {
    resumo: t(doc?.resumo),
    sobre: t(doc?.sobre),
    objetivo: t(doc?.objetivo),
    publico_alvo: t(doc?.publico_alvo),
    perfil_egresso: t(doc?.perfil_egresso),
    modalidade: t(doc?.modalidade),
    titulo_conferido: t(doc?.titulo_conferido),
    professor_lider: t(doc?.professor_lider),
    universidade: t(doc?.universidade),
    valor: t(doc?.valor),
    lancamento: t(doc?.lancamento),
    mec_url: t(doc?.mec_url),
    carga_horaria_fonte: n(doc?.carga_horaria_fonte),
    duracao_minima_fonte: n(doc?.duracao_minima_fonte),
    duracao_maxima_fonte: n(doc?.duracao_maxima_fonte),
    secoes: (doc?.secoes ?? []).map((s) => ({ titulo: s.titulo, texto: s.texto })),
  };
}

/** O que MUDOU entre a página do banco e o rascunho — e só isso vai. */
function diferenca(original: Rascunho, rascunho: Rascunho): CamposDaPagina {
  const campos: CamposDaPagina = {};
  for (const { chave } of [...TEXTOS, ...CURTOS]) {
    const antes = String(original[chave]).trim();
    const agora = String(rascunho[chave]).trim();
    if (antes !== agora) (campos as Record<string, unknown>)[chave] = agora;
  }
  for (const { chave } of NUMEROS) {
    const antes = original[chave].trim();
    const agora = rascunho[chave].trim();
    if (antes !== agora) {
      // Um número que não é número vai como está, e o BANCO diz o que há de
      // errado — validar aqui seria a segunda regra.
      (campos as Record<string, unknown>)[chave] = agora === "" ? "" : Number.isFinite(Number(agora)) ? Number(agora) : agora;
    }
  }
  const secoesAntes = JSON.stringify(original.secoes.map((s) => ({ titulo: s.titulo.trim(), texto: s.texto })));
  const secoesAgora = JSON.stringify(rascunho.secoes.map((s) => ({ titulo: s.titulo.trim(), texto: s.texto })));
  if (secoesAntes !== secoesAgora) campos.secoes = rascunho.secoes;
  return campos;
}

export default function AdminCatalogoProdutoPagina({
  curso,
  doc,
  aoSalvar,
  aoFechar,
}: {
  curso: string;
  doc: DocumentacaoDoCurso | null;
  aoSalvar: () => Promise<void>;
  aoFechar: () => void;
}) {
  const [rascunho, setRascunho] = useState<Rascunho>(() => daPagina(doc));
  const [motivo, setMotivo] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [recado, setRecado] = useState<{ tipo: "ok" | "erro"; texto: string } | null>(null);

  // A página que VOLTA do banco depois de salvar é a nova base da diferença:
  // sem isto, salvar duas vezes mandaria de novo o que já foi.
  const original = daPagina(doc);
  useEffect(() => {
    setRascunho(daPagina(doc));
  }, [doc?.atualizado_em, doc?.editado_em]); // eslint-disable-line react-hooks/exhaustive-deps

  const mudou = diferenca(original, rascunho);
  const quantos = Object.keys(mudou).length;

  function mudar<K extends keyof Rascunho>(chave: K, valor: Rascunho[K]) {
    setRascunho((r) => ({ ...r, [chave]: valor }));
  }

  function mudarSecao(i: number, parte: Partial<{ titulo: string; texto: string }>) {
    setRascunho((r) => ({ ...r, secoes: r.secoes.map((s, j) => (j === i ? { ...s, ...parte } : s)) }));
  }

  function moverSecao(i: number, para: number) {
    setRascunho((r) => {
      if (para < 0 || para >= r.secoes.length) return r;
      const s = [...r.secoes];
      const [x] = s.splice(i, 1);
      s.splice(para, 0, x);
      return { ...r, secoes: s };
    });
  }

  async function salvar() {
    setSalvando(true);
    setRecado(null);
    try {
      if (quantos === 0) {
        setRecado({ tipo: "ok", texto: "Nada mudou na página." });
        return;
      }
      const r = await editarPaginaDoProduto(curso, mudou, motivo.trim() || null);
      setRecado({ tipo: r.ok ? "ok" : "erro", texto: mensagemDoCatalogo(r) });
      if (r.ok) {
        setMotivo("");
        await aoSalvar();
      }
    } catch (e) {
      setRecado({ tipo: "erro", texto: mensagemDoCatalogo(null, e instanceof Error ? e.message : undefined) });
    } finally {
      setSalvando(false);
    }
  }

  return (
    <section className="surface rise p-6 sm:p-7" role="group" aria-labelledby="titulo-editar-pagina" data-editor-da-pagina>
      <h3 id="titulo-editar-pagina" className="font-display text-lg font-bold text-titulo">
        {doc ? "Editar a página do produto" : "Criar a página do produto"}
      </h3>
      <p className="mt-1 text-sm text-cinza">
        Desde 25/09/2026 esta página é editada aqui, e a carga do ClickUp não a sobrescreve. O
        texto aceita a marcação que já vinha de lá (títulos com #, listas com -).
      </p>

      <div className="mt-5 flex flex-col gap-4">
        {TEXTOS.map((c) => (
          <label key={c.chave} className="flex flex-col gap-1.5">
            <span className="text-sm font-semibold text-tinta">{c.rotulo}</span>
            <textarea
              data-campo-pagina={c.chave}
              rows={c.linhas ?? 3}
              className={`${CAMPO} resize-y`}
              value={String(rascunho[c.chave])}
              onChange={(e) => mudar(c.chave, e.target.value)}
            />
            {c.dica && <span className="text-xs text-cinza">{c.dica}</span>}
          </label>
        ))}

        <div className="grid gap-4 sm:grid-cols-2">
          {CURTOS.map((c) => (
            <label key={c.chave} className="flex flex-col gap-1.5">
              <span className="text-sm font-semibold text-tinta">{c.rotulo}</span>
              <input
                data-campo-pagina={c.chave}
                className={CAMPO}
                value={String(rascunho[c.chave])}
                onChange={(e) => mudar(c.chave, e.target.value)}
              />
              {c.dica && <span className="text-xs text-cinza">{c.dica}</span>}
            </label>
          ))}
        </div>

        <div>
          <div className="grid gap-4 sm:grid-cols-3">
            {NUMEROS.map((c) => (
              <label key={c.chave} className="flex flex-col gap-1.5">
                <span className="text-sm font-semibold text-tinta">{c.rotulo}</span>
                <input
                  data-campo-pagina={c.chave}
                  inputMode="numeric"
                  className={CAMPO}
                  value={rascunho[c.chave]}
                  onChange={(e) => mudar(c.chave, e.target.value)}
                />
              </label>
            ))}
          </div>
          {/* O número que a PÁGINA diz, e não o que o banco faz: quem manda na
              carga horária impressa e no prazo que a emissão cobra é o prazo da
              Faculdade. A tela de baixo compara os dois. */}
          <p className="mt-1.5 text-xs text-cinza">
            O que a página promete. Quem manda na carga horária impressa no diploma e no prazo que a
            emissão cobra é o banco — a tabela «A página e o banco» compara os dois.
          </p>
        </div>

        <div className="border-t border-borda pt-4">
          <h4 className="text-sm font-semibold text-tinta">Seções</h4>
          <p className="mt-0.5 text-xs text-cinza">
            O resto da página, na ordem em que aparece — a ementa costuma ser a maior.
          </p>
          <ol className="mt-3 space-y-3">
            {rascunho.secoes.map((s, i) => (
              <li key={i} className="rounded-cf border border-borda p-3" data-secao-editavel={i}>
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    data-secao-titulo={i}
                    aria-label={`Título da seção ${i + 1}`}
                    className={`${CAMPO} min-w-48 flex-1`}
                    value={s.titulo}
                    onChange={(e) => mudarSecao(i, { titulo: e.target.value })}
                  />
                  <button
                    type="button"
                    onClick={() => moverSecao(i, i - 1)}
                    disabled={i === 0}
                    aria-label={`Subir a seção ${i + 1}`}
                    className="btn-contorno min-h-9 px-3 text-xs disabled:opacity-40"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    onClick={() => moverSecao(i, i + 1)}
                    disabled={i === rascunho.secoes.length - 1}
                    aria-label={`Descer a seção ${i + 1}`}
                    className="btn-contorno min-h-9 px-3 text-xs disabled:opacity-40"
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    data-remover-secao={i}
                    onClick={() => setRascunho((r) => ({ ...r, secoes: r.secoes.filter((_, j) => j !== i) }))}
                    className="text-xs font-semibold text-cinza underline-offset-2 hover:text-tinta hover:underline"
                  >
                    Tirar
                  </button>
                </div>
                <textarea
                  data-secao-texto={i}
                  aria-label={`Texto da seção ${i + 1}`}
                  rows={6}
                  className={`${CAMPO} mt-2 w-full resize-y`}
                  value={s.texto}
                  onChange={(e) => mudarSecao(i, { texto: e.target.value })}
                />
              </li>
            ))}
          </ol>
          <button
            type="button"
            data-nova-secao
            onClick={() => setRascunho((r) => ({ ...r, secoes: [...r.secoes, { titulo: "", texto: "" }] }))}
            className="btn-contorno mt-3 min-h-10 text-sm"
          >
            + Seção
          </button>
        </div>

        <label className="flex flex-col gap-1.5 border-t border-borda pt-4">
          <span className="text-sm font-semibold text-tinta">Justifique a alteração</span>
          <input
            data-campo-pagina="motivo"
            className={CAMPO}
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Vai para o histórico do produto, ao lado do que mudou"
          />
        </label>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <button
          type="button"
          data-salvar-pagina
          disabled={salvando}
          onClick={() => void salvar()}
          className="pill-cta min-h-12 px-6 text-sm disabled:opacity-55"
        >
          {salvando ? "Salvando…" : doc ? "Salvar a página" : "Criar a página"}
        </button>
        <button type="button" data-fechar-pagina onClick={aoFechar} className="btn-contorno min-h-12 text-sm">
          Fechar
        </button>
        <span className="text-xs text-cinza" data-campos-mudados={quantos}>
          {quantos === 0 ? "Nada mudou ainda." : `${quantos} campo(s) mudaram e vão ser salvos.`}
        </span>
      </div>
      {recado && <Recado tipo={recado.tipo} texto={recado.texto} />}
    </section>
  );
}
